import React, { useState, useRef } from 'react'
import style from './upload-image.module.scss'
import EyeIcon from '../../../../../../../../assets/svg/solo-chat/image-eye.svg'
import DeleteIcon from '../../../../../../../../assets/svg/solo-chat/image-delete.svg'
import { Select } from '../../../../components'
import ImgIcon from '../../../../../../../../assets/svg/solo-chat/add-image.svg'
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks'
import { api } from '../../api'
import { useToast } from '../../../../../../../../helpers/commonFunction'
import { Icon, Spin } from 'antd'
import { isUploadedFromStudio } from '../../utils'
import { allowedContentTypes, maxSize } from '../../../../constants'

type Props = {
	mode: "view" | "edit" | "create"
	onChange: (url: string) => void
	url: string
	error?: string
	clearError?: () => void
}

export function UploadImage(props: Props) {
  const canDelete = usePermissions('content.Delete')
	const [appId] = useAppAndBranchId()
	const { ErrorToast, WarningToast } = useToast()
	const { mode, onChange, url, error = "", clearError = () => {} } = props
	const inputRef = useRef<HTMLInputElement>(null)
	const [type, setType] = useState<"upload" | "url">("upload")
	const { mutate: uploadFile, loading: uploadLoading } = useMutate(
		api.uploadFile,
	);

	async function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
		if(e.target.files?.length) {
		  clearError()
			const file = e.target.files[0]
      if(!allowedContentTypes.image.includes(file.type)) {
        WarningToast("Please select a valid image")
        return
      }
      if(file.size > maxSize.image) {
        WarningToast("Please select a image less than 5MB")
        return
      }
			try {
				const url = await uploadFile(appId, file, file.type)
				onChange(url)
			} catch(err) {
				ErrorToast("Failed to upload image")
			} finally {
				if(inputRef.current) {
					inputRef.current.value = ""
				}
			}
		}
	}

	const selectStyles: Record<string, string> = {
		'--select-top': '6px',
		'--select-options-top': '23px',
		'--select-right': type === "url" ? '18px' : '30px',
		'--select-chat-border': 'none',
		'--select-space': '0px',
		'--select-width': '150px',
	}

	return (
		<div className={style.uploadImage}>
			<div className={style.select} style={selectStyles}>
				<Select required options={options} placeholder="Select" value={type} onSelect={(value: any) => setType(value)} />
			</div>

			{type == "upload" && <div className={style.upload}>
				<input onChange={onFilePick} accept="image/*" ref={inputRef} id="picker" type="file" />
				{!url && (
					<button onClick={() => {
						if (inputRef.current) {
							inputRef.current.click()
						}
					}} className={style.frame}>
						{(uploadLoading) ? (
							<Spin indicator={<Icon type="loading" style={{ fontSize: 24, color: '#15808C' }} spin />} />
						) : (
							<img src={ImgIcon} alt="add image" />
						)}
					</button>
				)}

				{url && (
					<div className={style.frame} data-media="image">
						<img src={url} alt="preview" />
						<div className={style.actions}>
							<button onClick={() => {
								window.open(url, '_blank')
							}}>
								<img src={EyeIcon} alt="view" />
							</button>
							{mode !== "view" && canDelete && (
								<button onClick={async () => {
									if(inputRef.current) {
										onChange("")
										inputRef.current.value = ""
										if(isUploadedFromStudio(url)) {
											api.deleteFile(appId, url)
										}
									}
								}}>
									<img src={DeleteIcon} alt="delete" />
								</button>
							)}
						</div>
					</div>
				)}
				{error && <span className={style.error}>{error}</span>}
			</div>}

			{type === "url" && <div className={style.url}>
				<input autoFocus disabled={mode === "view"} id="url" placeholder="Enter url" value={url} onChange={(e) => {
					clearError()
					const url = e.currentTarget.value
					onChange(url)
				}} />
				{error && <span className={style.error}>{error}</span>}
			</div>}
		</div>
	)
}

const options = [
	{ label: "Upload Image", value: "upload" },
	{ label: "Add Image URL", value: "url" },
]
